import { Component, OnInit, OnDestroy, Input, AfterViewInit, ViewChild } from '@angular/core';

import { Observable, Subscription, Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TreeNode } from 'primeng/api';

import { FullProfileAnimationModel } from '@Core/Dialogs/models/full-profile-animation.model';
import { AbstractDialogComponent } from '@Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '@Core/Dialogs/services/dialog.service';
import { ButtonTypesEnum } from '@Common/Forms/enums/button-types.enum';
import { IncludeOrExclude } from '@Common/Dialogs/services/data-select-dialog.service.interface';
import { JournodexSubjectsService } from '@Common/Journodex/services/journodex-subjects.service';
import { LocalizationService } from '@Core/Localization/services/localization.service';
import { messageSeverityEnum } from '@Common/Misc/enums/message-severity.enum';
import { ScrollPanelComponent } from '@Common/Basic/components/ScrollPanel/scroll-panel.component';
import { JournodexEnumsService } from '@Discover/Core/services/journodex-enums.service';
import { MediaMarket } from '@Discover/Core/models/media-market.model';
import { MediaMarketTypeEnum } from '@api/journodex/enum/media-market-type.enum';


interface ChipGroup {
    heading: string;
    type: MediaMarketTypeEnum;
    chips: Chip[];
}

interface Chip {
    id: number;
    label: string;
}

@Component({
    selector: 'agpr-media-market-dialog',
    templateUrl: './media-market-dialog.component.html',
    styleUrls: ['./media-market-dialog.component.scss'],
    standalone: false,
}) export class MediaMarketDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy, AfterViewInit {

    @Input() animationTrigger: Observable<FullProfileAnimationModel>;
    @Input() includeExclude: IncludeOrExclude;

    @Input() selectedMarkets: MediaMarket[] = [];
    @Input() scrollToChildId: number | null = null;
    @Input() set initialFilterString(query: string) {
        if (query) {
            this.filterString = query;
        }
    }

    @ViewChild('scrollPanel') scrollPanel: ScrollPanelComponent;

    overlayContainer: Element;
    filterString: string = '';

    /** tree like structure with parent regions and children media markets */
    mediaMarketOptions: TreeNode<MediaMarket>[] = [];

    /** flat children nodes */
    selectedMediaMarkets: TreeNode<MediaMarket>[] = [];

    chipGroups: ChipGroup[] = [];

    emptySubjects: boolean = false;

    private subscriptions: Subscription[] = [];
    private filterChanged$ = new Subject<void>();

    readonly id = 'mediaMarketDialog';
    readonly buttonType = ButtonTypesEnum;
    readonly messageSeverityEnum = messageSeverityEnum;

    constructor(
        coreDialogService: CoreDialogService,
        protected journodexSubjectsService: JournodexSubjectsService,
        protected journodexEnumsService: JournodexEnumsService,
        protected localizationService: LocalizationService
    ) {
        super(coreDialogService, 'MediaMarketDialogComponent');
    }

    ngOnInit() {
        this.overlayContainer = document.getElementById('main-content');

        this.subscriptions.push(
            this.journodexEnumsService.getAllMediaMarkets().subscribe(mediaMarkets => {
                this.mediaMarketOptions = this.constructTree(mediaMarkets);
                this.selectedMediaMarkets = this.mapSelectedToNodes(this.selectedMarkets, this.mediaMarketOptions);

                this.selectionChange();

                if (this.filterString) {
                    this.filterItems();
                }
            }),
            this.filterChanged$.pipe(debounceTime(300)).subscribe(() => {
                this.filterItems();
            })
        );
    }

    ngAfterViewInit() {
        this.scrollToChild();
    }

    filterChange() {
        this.filterChanged$.next();
    }

    apply() {
        this.closeDialog(this.selectedMediaMarkets.map(x => x.data));
    }

    resetAll() {
        this.selectedMediaMarkets = [];
        this.chipGroups = [];
    }


    /** recalculate chipGroups based on the current media market selection */
    selectionChange() {
        this.chipGroups = [
            {
                heading: 'United States',
                type: MediaMarketTypeEnum.US_MEDIA_MARKETS,
                chips: []
            },
            {
                heading: 'United Kingdom',
                type: MediaMarketTypeEnum.UK_MEDIA_MARKETS,
                chips: []
            },
            {
                heading: 'Canada',
                type: MediaMarketTypeEnum.CA_MEDIA_MARKETS,
                chips: []
            }
        ];

        const usParent = this.chipGroups.find(x => x.type === MediaMarketTypeEnum.US_MEDIA_MARKETS);
        const ukParent = this.chipGroups.find(x => x.type === MediaMarketTypeEnum.UK_MEDIA_MARKETS);
        const cadParent = this.chipGroups.find(x => x.type === MediaMarketTypeEnum.CA_MEDIA_MARKETS);


        for (const mediaMarket of this.selectedMediaMarkets) {
            switch(mediaMarket.type) {
                case MediaMarketTypeEnum.US_MEDIA_MARKETS: {
                    usParent.chips.push({
                        id: +mediaMarket.key,
                        label: mediaMarket.label
                    });

                    break;
                }
                case MediaMarketTypeEnum.UK_MEDIA_MARKETS: {
                    ukParent.chips.push({
                        id: +mediaMarket.key,
                        label: mediaMarket.label
                    });

                    break;
                }
                case MediaMarketTypeEnum.CA_MEDIA_MARKETS: {
                    cadParent.chips.push({
                        id: +mediaMarket.key,
                        label: mediaMarket.label
                    });

                    break;
                }
            }
        }

        usParent.chips.sort((a, b) => a.label.localeCompare(b.label));
        ukParent.chips.sort((a, b) => a.label.localeCompare(b.label));
        cadParent.chips.sort((a, b) => a.label.localeCompare(b.label));

    }


    removeChip(chip: Chip) {
        this.selectedMediaMarkets = this.selectedMediaMarkets.filter(x => +x.key !== chip.id);
        this.selectionChange();
    }


    /**
     * Converts mediaMarket array to TreeNode array and sort children alphabetically
     * @param mediaMarkets the models to convert
     *
     * @returns TreeNode[] with parent regions and media markets contained in children property
     */
    private constructTree(mediaMarkets: MediaMarket[]): TreeNode[] {
        const convertedArray: TreeNode[] = [
            {
                label: 'United States',
                data: null,
                type: MediaMarketTypeEnum.US_MEDIA_MARKETS,
                children: [],
                selectable: false,
                expanded: true
            },
            {
                label: 'United Kingdom',
                data: null,
                type: MediaMarketTypeEnum.UK_MEDIA_MARKETS,
                children: [],
                selectable: false,
                expanded: true
            },
            {
                label: 'Canada',
                data: null,
                type: MediaMarketTypeEnum.CA_MEDIA_MARKETS,
                children: [],
                selectable: false,
                expanded: true
            }
        ];

        const usParent = convertedArray.find(x => x.type === MediaMarketTypeEnum.US_MEDIA_MARKETS);
        const ukParent = convertedArray.find(x => x.type === MediaMarketTypeEnum.UK_MEDIA_MARKETS);
        const cadParent = convertedArray.find(x => x.type === MediaMarketTypeEnum.CA_MEDIA_MARKETS);


        for (const mediaMarket of mediaMarkets) {
            switch(mediaMarket.type) {
                case MediaMarketTypeEnum.US_MEDIA_MARKETS: {
                    usParent.children.push({
                        key: mediaMarket.id.toString(),
                        label: this.localizationService.transform(mediaMarket.displayString),
                        data: mediaMarket,
                        type: mediaMarket.type,
                        selectable: true
                    });

                    break;
                }
                case MediaMarketTypeEnum.UK_MEDIA_MARKETS: {
                    ukParent.children.push({
                        key: mediaMarket.id.toString(),
                        label: this.localizationService.transform(mediaMarket.displayString),
                        data: mediaMarket,
                        type: mediaMarket.type,
                        selectable: true
                    });

                    break;
                }
                case MediaMarketTypeEnum.CA_MEDIA_MARKETS: {
                    cadParent.children.push({
                        key: mediaMarket.id.toString(),
                        label: this.localizationService.transform(mediaMarket.displayString),
                        data: mediaMarket,
                        type: mediaMarket.type,
                        selectable: true
                    });

                    break;
                }
            }
        }

        usParent.children.sort(this.sortTreeItemsAlphabetically);
        ukParent.children.sort(this.sortTreeItemsAlphabetically);
        cadParent.children.sort(this.sortTreeItemsAlphabetically);

        return convertedArray;
    }


    /**
     * Convert the MediaMarket models to the TreeNode<MediaMarket>.
     * The mediaMarketOptions reference must be used for agpr-tree-list to work.
     */
    private mapSelectedToNodes(mediaMarkets: MediaMarket[], mediaMarketOptions: TreeNode<MediaMarket>[]): TreeNode<MediaMarket>[] {
        const treeNodes = [];
        mediaMarkets?.forEach(mediaMarket => {
            const ukParent = mediaMarketOptions.find(x => x.type === MediaMarketTypeEnum.UK_MEDIA_MARKETS);
            const usParent = mediaMarketOptions.find(x => x.type === MediaMarketTypeEnum.US_MEDIA_MARKETS);
            const cadParent = mediaMarketOptions.find(x => x.type === MediaMarketTypeEnum.CA_MEDIA_MARKETS);


            switch(mediaMarket.type) {
                case MediaMarketTypeEnum.UK_MEDIA_MARKETS: {
                    const child = ukParent.children.find(x => x.key === mediaMarket.id.toString());
                    if (child) {
                        ukParent.expanded = true;
                        treeNodes.push(child);
                    }

                    break;
                }
                case MediaMarketTypeEnum.US_MEDIA_MARKETS: {
                    const child = usParent.children.find(x => x.key === mediaMarket.id.toString());
                    if (child) {
                        usParent.expanded = true;
                        treeNodes.push(child);
                    }

                    break;
                }
                case MediaMarketTypeEnum.CA_MEDIA_MARKETS: {
                    const child = cadParent.children.find(x => x.key === mediaMarket.id.toString());
                    if (child) {
                        cadParent.expanded = true;
                        treeNodes.push(child);
                    }

                    break;
                }
            }
        });

        return treeNodes;
    }

    /**
     * Filter the tree list and populate the suggested list
     */
    private filterItems() {
        const query = this.filterString.toLocaleLowerCase();
        const filteredChildNodes: TreeNode[] = [];

        this.emptySubjects = true;

        if (this.filterString.length === 0) {
            this.mediaMarketOptions.forEach(parent => {
                parent.styleClass = '';
                parent.expanded = true;
                parent.children?.forEach(child => {
                    child.styleClass = '';
                    child.expanded = false;
                });
            });
            this.emptySubjects = false;
            return;
        }

        this.mediaMarketOptions.forEach(parent => {
            parent.styleClass = 'hide';
            let childFound = false;

            parent.children?.forEach(child => {
                if (child.label.toLocaleLowerCase().includes(query)) {
                    child.styleClass = '';
                    childFound = true;
                    filteredChildNodes.push(child);
                } else {
                    child.styleClass = 'hide';
                }
            });

            if (childFound || parent.label.toLocaleLowerCase().includes(query)) {
                parent.expanded = true;
                parent.styleClass = '';
                this.emptySubjects = false;
            }
        });
    }


    private scrollToChild() {
        if (!this.scrollToChildId) {
            this.mediaMarketOptions.forEach(x => x.expanded = false);
            return;
        }

        for (const [index, parent] of this.mediaMarketOptions.entries()) {
            const childIndex = parent.children.findIndex(node => node.key === this.scrollToChildId.toString());

            if (childIndex >= 0) {
                parent.expanded = true;

                // There is one tree with a root node, and then a subnode of root-children which actually holds the children
                const tree = this.scrollPanel.sp.el.nativeElement.querySelectorAll('.p-tree-root-children')?.[0];
                const subTreeChildren = tree.querySelectorAll('.p-tree-node-children')?.[index];
                const childNode = subTreeChildren.children?.[childIndex];

                if (childNode) {
                    this.scrollPanel.scrollIntoViewNative(childNode);
                }

                break;
            }
        }
    }

    private sortTreeItemsAlphabetically(a: TreeNode, b: TreeNode): number {
        const labelA = a.label.toLowerCase();
        const labelB = b.label.toLowerCase();

        return (labelA < labelB) ? -1 : (labelA > labelB) ? 1 : 0;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
